import { useState } from "react";
import { View, Text, ScrollView, Pressable } from "react-native";
import { ChevronLeft, Snowflake } from "lucide-react-native";
import { useNavigation } from "@react-navigation/native";
import {
  colors,
  typography,
  spacing,
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { EMBER } from "../../../onboarding/theme";

import WhatToDoCard from "../../components/plan/WhatToDoCard";
import WhatNotToDoCard from "../../components/plan/WhatNotToDoCard";
import MoneySideSection from "../../components/plan/MoneySideSection";

import DonorRegistryHeader from "../../components/access/DonorRegistryHeader";
import PharmacyHeader from "../../components/access/PharmacyHeader";

import DeliveryTrackingCard from "../../screens/care/sections/DeliveryTrackingCard";
import { ActionCard } from "../../screens/CryoVaultScreen";

import CompareClinicCostsCard from "../../screens/CostsScreen/CompareClinicCostsCard";

import CoverageBenefits from "../../screens/YouScreen/sections/CoverageBenefits";
import PersonalMedicalInfo from "../../screens/YouScreen/sections/PersonalMedicalInfo";
import CareContext, {
  type MedicalFertilityDestination,
} from "../../screens/YouScreen/sections/CareContext";
import InsuranceCard from "../../screens/YouScreen/sections/InsuranceCard";
import ConnectionsSharing from "../../screens/YouScreen/sections/ConnectionsSharing";
import InviteFriend from "../../screens/YouScreen/sections/InviteFriend";
import RemindersNotifications from "../../screens/YouScreen/sections/RemindersNotifications";
import Integrations from "../../screens/YouScreen/sections/Integrations";
import ResearchStudies from "../../screens/YouScreen/sections/ResearchStudies";
import DataSharingModal from "../../screens/YouScreen/modals/DataSharingModal";

import RoleAdaptedHomeBanner from "../../components/RoleAdaptedHomeBanner";
import CompensationPlaceholderCard from "../../components/CompensationPlaceholderCard";

import VideoEmbed from "../../components/article/VideoEmbed";
import DiagramPlaceholder from "../../components/article/DiagramPlaceholder";
import SupplementOrderingNote from "../../components/SupplementOrderingNote";

import { AddPrompt } from "../../screens/PastCycleDetailScreen";

import type {
  ConnectionWithSharing,
  Study,
  PersonalMedicalData,
  InsuranceInfo,
  YouScreenAction,
} from "../../screens/YouScreen/types";
import { DEV_CARE_CONNECTIONS } from "../../screens/YouScreen/devCareConnections";

const NOOP_DISPATCH: React.Dispatch<YouScreenAction> = () => {};
const NOOP_CARE_NAVIGATION = (_destination: MedicalFertilityDestination) => {};
const NOOP = () => {};

const DEV_PERSONAL_MEDICAL: PersonalMedicalData = {
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
  dateOfBirth: null,
  gender: null,
  emergencyContact: null,
  bloodType: null,
  allergies: [],
  hasAllergies: null,
  address: null,
};

const DEV_INSURANCE: InsuranceInfo = {
  provider: null,
  planName: null,
  memberId: null,
};

const DEV_CONNECTIONS: ConnectionWithSharing[] = [
  {
    type: "partner",
    name: null,
    status: "not_connected",
    permissions: {
      medicalHistory: false,
      testResults: false,
      medications: false,
      cycleData: false,
      appointments: false,
      documents: false,
    },
  },
  {
    type: "clinic",
    name: null,
    status: "not_connected",
    permissions: {
      medicalHistory: false,
      testResults: false,
      medications: false,
      cycleData: false,
      appointments: false,
      documents: false,
    },
  },
  {
    type: "pharmacy",
    name: null,
    status: "not_connected",
    permissions: {
      medicalHistory: false,
      testResults: false,
      medications: false,
      cycleData: false,
      appointments: false,
      documents: false,
    },
  },
  {
    type: "provider",
    name: null,
    status: "not_connected",
    permissions: {
      medicalHistory: false,
      testResults: false,
      medications: false,
      cycleData: false,
      appointments: false,
      documents: false,
    },
  },
];

const DEV_STUDIES: Study[] = [];

const TOTAL_COUNT = 25;

function SectionTitle({ children }: { children: string }) {
  return (
    <Text
      style={{
        color: colors.text,
        fontFamily: typography.families.sansBold,
        fontSize: typography.sizes.xl,
        letterSpacing: -0.3,
      }}
    >
      {children}
    </Text>
  );
}

function SourceLabel({ file }: { file: string }) {
  return (
    <Text
      style={{
        color: colors.textFaint,
        fontFamily: typography.families.mono,
        fontSize: typography.sizes.xs,
        marginTop: 4,
      }}
    >
      {file}
    </Text>
  );
}

export default function DevComingSoonInventoryScreen() {
  const navigation = useNavigation();
  const [dataSharingOpen, setDataSharingOpen] = useState(false);
  const [anonymousDataContribution, setAnonymousDataContribution] =
    useState(false);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          padding: spacing.lg,
          paddingTop: SCREEN_PADDING_TOP + spacing.lg,
          gap: spacing["2xl"],
          maxWidth: 390,
          width: "100%",
          alignSelf: "center",
        }}
      >
        <Pressable
          onPress={() => navigation.goBack()}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: spacing.xs,
          }}
        >
          <ChevronLeft size={20} color={colors.text} />
          <Text
            style={{
              color: colors.text,
              fontFamily: typography.families.sansMedium,
              fontSize: typography.sizes.md,
            }}
          >
            Back
          </Text>
        </Pressable>

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: spacing.sm,
          }}
        >
          <Text
            style={{
              color: colors.text,
              fontFamily: typography.families.sansBold,
              fontSize: typography.sizes["2xl"],
              letterSpacing: -0.3,
            }}
          >
            Coming Soon Inventory
          </Text>
          <View
            style={{
              backgroundColor: colors.warm,
              borderRadius: 12,
              paddingHorizontal: spacing.sm,
              paddingVertical: 2,
            }}
          >
            <Text
              style={{
                color: colors.surface,
                fontFamily: typography.families.sansBold,
                fontSize: typography.sizes.sm,
              }}
            >
              {TOTAL_COUNT}
            </Text>
          </View>
        </View>

        <View style={{ gap: spacing.lg }}>
          <SectionTitle>Plan</SectionTitle>
          <View>
            <WhatToDoCard />
            <SourceLabel file="PlanHomeScreen.tsx" />
          </View>
          <View>
            <WhatNotToDoCard />
            <SourceLabel file="PlanHomeScreen.tsx" />
          </View>
          <View>
            <MoneySideSection />
            <SourceLabel file="PlanHomeScreen.tsx" />
          </View>
        </View>

        <View style={{ gap: spacing.lg }}>
          <SectionTitle>Access</SectionTitle>
          <View>
            <DonorRegistryHeader />
            <SourceLabel file="DonorRegistryScreen.tsx" />
          </View>
          <View>
            <PharmacyHeader />
            <SourceLabel file="PharmacyScreen.tsx" />
          </View>
        </View>

        <View style={{ gap: spacing.lg }}>
          <SectionTitle>Care</SectionTitle>
          <View>
            <DeliveryTrackingCard />
            <SourceLabel file="DeliveryTrackingCard.tsx" />
          </View>
          <View>
            <ActionCard
              icon={
                <Snowflake
                  size={18}
                  color={EMBER.colors.teal}
                  strokeWidth={2}
                />
              }
              label="Transfer to new facility"
              subtitle="Move your stored eggs or embryos"
              comingSoon
            />
            <SourceLabel file="CryoVaultScreen.tsx" />
          </View>
        </View>

        <View style={{ gap: spacing.lg }}>
          <SectionTitle>Costs</SectionTitle>
          <View>
            <CompareClinicCostsCard />
            <SourceLabel file="CompareClinicCostsCard.tsx" />
          </View>
        </View>

        <View style={{ gap: spacing.lg }}>
          <SectionTitle>You (Profile)</SectionTitle>
          <View>
            <CoverageBenefits
              policies={[]}
              primaryPolicy={undefined}
              hasInsurance={false}
              coverageState="empty"
              verificationStatus="unverified"
              fertilityCoverage={null}
              isLoading={false}
              isError={false}
              onRetry={NOOP}
            />
            <SourceLabel file="CoverageBenefits.tsx" />
          </View>
          <View>
            <PersonalMedicalInfo
              data={DEV_PERSONAL_MEDICAL}
              onNavigate={NOOP_CARE_NAVIGATION}
            />
            <SourceLabel file="PersonalMedicalInfo.tsx" />
          </View>
          <View>
            <CareContext onNavigate={NOOP_CARE_NAVIGATION} />
            <SourceLabel file="CareContext.tsx" />
          </View>
          <View>
            <InsuranceCard insuranceInfo={DEV_INSURANCE} />
            <SourceLabel file="InsuranceCard.tsx" />
          </View>
          <View>
            <ConnectionsSharing
              connections={DEV_CONNECTIONS}
              dispatch={NOOP_DISPATCH}
              careConnectionsOverride={DEV_CARE_CONNECTIONS}
            />
            <SourceLabel file="ConnectionsSharing.tsx" />
          </View>
          <View>
            <InviteFriend />
            <SourceLabel file="InviteFriend.tsx" />
          </View>
          <View>
            <RemindersNotifications
              notificationPreferences={null}
              notificationPreferencesStatus={{
                isLoading: true,
                hasLoadError: false,
                hasSaveError: false,
                retryLoad: NOOP,
                retrySave: NOOP,
              }}
              dispatch={NOOP_DISPATCH}
            />
            <SourceLabel file="RemindersNotifications.tsx" />
          </View>
          <View>
            <Integrations />
            <SourceLabel file="Integrations.tsx" />
          </View>
          <View>
            <ResearchStudies
              studies={DEV_STUDIES}
              anonymousDataContribution={anonymousDataContribution}
              anonymousDataContributionLoadFailed={false}
              anonymousDataContributionSaveFailed={false}
              onAnonymousDataContributionChange={setAnonymousDataContribution}
            />
            <SourceLabel file="ResearchStudies.tsx" />
          </View>
          <View>
            <Pressable
              onPress={() => setDataSharingOpen(true)}
              style={[
                GLASS_CARD_STYLE,
                { padding: spacing.lg, alignItems: "center" },
              ]}
            >
              <Text
                style={{
                  color: colors.text,
                  fontFamily: typography.families.sansMedium,
                  fontSize: typography.sizes.base,
                }}
              >
                Open Data Sharing Modal
              </Text>
            </Pressable>
            <SourceLabel file="DataSharingModal.tsx" />
          </View>
        </View>

        <View style={{ gap: spacing.lg }}>
          <SectionTitle>Home</SectionTitle>
          <View>
            <RoleAdaptedHomeBanner />
            <SourceLabel file="RoleAdaptedHomeBanner.tsx" />
          </View>
          <View>
            <CompensationPlaceholderCard role="donor" />
            <SourceLabel file="CompensationPlaceholderCard.tsx" />
          </View>
        </View>

        <View style={{ gap: spacing.lg }}>
          <SectionTitle>Content</SectionTitle>
          <View>
            <VideoEmbed caption="How IVF works" />
            <SourceLabel file="VideoEmbed.tsx" />
          </View>
          <View>
            <DiagramPlaceholder
              label="Hormone cycle"
              description="Estrogen and progesterone levels during stimulation"
            />
            <SourceLabel file="DiagramPlaceholder.tsx" />
          </View>
          <View>
            <SupplementOrderingNote />
            <SourceLabel file="SupplementDetailSheet.tsx" />
          </View>
        </View>

        <View style={{ gap: spacing.lg }}>
          <SectionTitle>Misc</SectionTitle>
          <View>
            <AddPrompt label="Edit cycle data" />
            <SourceLabel file="PastCycleDetailScreen.tsx" />
          </View>
        </View>
      </ScrollView>

      <DataSharingModal
        visible={dataSharingOpen}
        connectionType="clinic"
        connections={DEV_CONNECTIONS}
        dispatch={NOOP_DISPATCH}
        onClose={() => setDataSharingOpen(false)}
      />
    </GlassScreenLayout>
  );
}
